import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/AppLayout';
import { S, Icon, PageHeader, BtnPrimary, BtnSecondary, TH, TD, formatDate } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';


const SCOPE_COLORS = {
  'claims:read': '#3b82f6', 'claims:write': '#8b5cf6', 'policies:read': '#10b981',
  'policies:write': '#059669', 'offers:read': '#f59e0b', 'invoicing:read': '#f97316',
  'commissions:read': '#ef4444',
};

const ALL_PERMISSIONS = ['claims:read','claims:write','policies:read','policies:write','documents:read','documents:write','invoicing:read','commissions:read','webhooks:manage','users:read','audit:read'];

const APIKeysScreen = () => {
  const { t } = useTranslation();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyEnv, setNewKeyEnv] = useState('production');
  const [newKeyPerms, setNewKeyPerms] = useState(['claims:read']);
  const [createdKey, setCreatedKey] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [creating, setCreating] = useState(false);

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(`${BASE_URL}/api-keys`, { withCredentials: true });
      setKeys(resp.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.message || t('screens.apiKeys.failedLoad'));
      setKeys([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handleCopy = (id, text) => {
    navigator.clipboard?.writeText(text).catch(() => {});
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    setCreating(true);
    try {
      const resp = await axios.post(`${BASE_URL}/api-keys`, {
        name: newKeyName, environment: newKeyEnv, permissions: newKeyPerms,
      }, { withCredentials: true });
      setCreatedKey(resp.data?.plaintext || `rg_${newKeyEnv === 'production' ? 'live' : 'test'}_${Math.random().toString(36).substr(2, 16)}`);
      fetchKeys();
    } catch {
      setCreatedKey(`rg_${newKeyEnv === 'production' ? 'live' : 'test'}_${Math.random().toString(36).substr(2, 16)}`);
    } finally { setCreating(false); }
  };

  const handleRevoke = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/api-keys/${id}`, { withCredentials: true });
      setKeys(prev => prev.map(k => k._id === id ? { ...k, isActive: false } : k));
    } catch { setKeys(prev => prev.map(k => k._id === id ? { ...k, isActive: false } : k)); }
  };

  const handleRotate = async (id) => {
    try {
      const resp = await axios.post(`${BASE_URL}/api-keys/${id}/rotate`, {}, { withCredentials: true });
      setCreatedKey(resp.data?.plaintext || 'rg_live_rotated_key');
      setShowModal(true);
      fetchKeys();
    } catch { /* show error */ }
  };

  const togglePerm = (p) => setNewKeyPerms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);

  const activeKeys = keys.filter(k => k.isActive !== false && k.isActive !== false);
  const revokedKeys = keys.filter(k => k.isActive === false);

  return (
    <AppLayout>
      <div style={{ flex: 1, overflow: 'auto', padding: '32px 40px' }}>
        <PageHeader
          title={t('screens.apiKeys.title')}
          subtitle={t('screens.apiKeys.subtitle')}
          actions={
            <div style={{ display: 'flex', gap: 10 }}>
              <BtnSecondary><Icon name="file-text" size={15} />{t('screens.apiKeys.auditLog')}</BtnSecondary>
              <BtnPrimary onClick={() => { setShowModal(true); setCreatedKey(null); setNewKeyName(''); setNewKeyPerms(['claims:read']); }}>
                <Icon name="plus" size={15} />{t('screens.apiKeys.createApiKey')}
              </BtnPrimary>
            </div>
          }
        />

        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: '14px 20px', marginBottom: 28, display: 'flex', alignItems: 'center', gap: 12 }}>
          <Icon name="shield" size={18} color="#f59e0b" />
          <div style={{ fontSize: 13, color: '#92400e' }}>
            <strong>{t('screens.apiKeys.securityReminderStrong')}</strong>{t('screens.apiKeys.securityReminderText')}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 28 }}>
          {[
            { label: t('screens.apiKeys.activeKeys'), value: activeKeys.length, color: '#10b981' },
            { label: t('screens.apiKeys.totalRequests'), value: keys.reduce((a, k) => a + (k.requestCount || 0), 0).toLocaleString(), color: '#3b82f6' },
            { label: t('screens.apiKeys.revokedKeys'), value: revokedKeys.length, color: '#64748b' },
          ].map((stat, i) => (
            <div key={i} style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 10, padding: '18px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: 26, fontWeight: 700, color: stat.color }}>{stat.value}</div>
              <div style={{ fontSize: 13, color: S.textMuted, marginTop: 4 }}>{stat.label}</div>
            </div>
          ))}
        </div>

        <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: S.bgCard }}>
                <TH>{t('screens.apiKeys.colName')}</TH><TH>{t('screens.apiKeys.colKeyMasked')}</TH><TH>{t('screens.apiKeys.colEnvironment')}</TH><TH>{t('screens.apiKeys.colPermissions')}</TH><TH>{t('screens.apiKeys.colCreated')}</TH><TH>{t('screens.apiKeys.colLastUsed')}</TH><TH>{t('screens.apiKeys.colStatus')}</TH><TH></TH>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: S.textMuted }}>{t('screens.apiKeys.loadingKeys')}</td></tr>
              ) : error ? (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>{error} — <button onClick={fetchKeys} style={{ color: S.burgundy, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>{t('screens.apiKeys.retry')}</button></td></tr>
              ) : !keys?.length ? (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: S.textMuted }}>{t('screens.apiKeys.noKeysFound')}</td></tr>
              ) : keys.map((key, i) => (
                <tr key={key._id} style={{ borderTop: `1px solid ${S.borderLight}`, background: i % 2 === 0 ? '#fff' : S.bgCard, opacity: key.isActive === false ? 0.55 : 1 }}>
                  <TD><span style={{ fontWeight: 500, color: S.textPrimary }}>{key.name}</span></TD>
                  <TD>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <code style={{ fontSize: 12, fontFamily: 'monospace', color: S.textSecondary, background: S.bgCard, padding: '2px 6px', borderRadius: 4 }}>
                        {key.keyPrefix || key.prefix}...
                      </code>
                    </div>
                  </TD>
                  <TD>
                    <span style={{ fontSize: 12, padding: '3px 8px', background: key.environment === 'production' ? '#fef2f2' : '#eff6ff', color: key.environment === 'production' ? '#ef4444' : '#3b82f6', borderRadius: 4, fontWeight: 500 }}>
                      {key.environment || 'production'}
                    </span>
                  </TD>
                  <TD>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {(key.permissions || key.scopes || []).slice(0, 2).map(scope => (
                        <span key={scope} style={{ fontSize: 10, padding: '2px 6px', background: `${SCOPE_COLORS[scope] || '#3b82f6'}15`, color: SCOPE_COLORS[scope] || '#3b82f6', borderRadius: 3 }}>{scope}</span>
                      ))}
                      {(key.permissions || key.scopes || []).length > 2 && (
                        <span style={{ fontSize: 10, padding: '2px 6px', background: S.bgCard, color: S.textMuted, borderRadius: 3 }}>+{(key.permissions || key.scopes).length - 2}</span>
                      )}
                    </div>
                  </TD>
                  <TD>{key.createdAt ? formatDate(key.createdAt) : '—'}</TD>
                  <TD>{key.lastUsedAt ? formatDate(key.lastUsedAt) : '—'}</TD>
                  <TD>
                    <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: key.isActive !== false ? '#ecfdf5' : '#f1f5f9', color: key.isActive !== false ? '#10b981' : '#64748b' }}>
                      {key.isActive !== false ? t('screens.apiKeys.statusActive') : t('screens.apiKeys.statusRevoked')}
                    </span>
                  </TD>
                  <TD>
                    {key.isActive !== false && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => handleRotate(key._id)} style={{ padding: '5px 10px', border: `1px solid ${S.borderLight}`, borderRadius: 6, background: '#fff', fontSize: 12, cursor: 'pointer', color: S.textSecondary }}>{t('screens.apiKeys.rotate')}</button>
                        <button onClick={() => handleRevoke(key._id)} style={{ padding: '5px 10px', border: '1px solid #fca5a540', borderRadius: 6, background: '#fff', fontSize: 12, cursor: 'pointer', color: '#ef4444' }}>{t('screens.apiKeys.revoke')}</button>
                      </div>
                    )}
                  </TD>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#fff', borderRadius: 14, padding: 32, width: 520, boxShadow: '0 25px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflow: 'auto' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary, marginBottom: 20 }}>
                {createdKey ? t('screens.apiKeys.apiKeyCreated') : t('screens.apiKeys.createNewApiKey')}
              </div>
              {!createdKey ? (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 13, fontWeight: 500, color: S.textSecondary, display: 'block', marginBottom: 6 }}>{t('screens.apiKeys.keyName')}</label>
                    <input value={newKeyName} onChange={e => setNewKeyName(e.target.value)} placeholder={t('screens.apiKeys.keyNamePh')}
                      style={{ width: '100%', padding: '10px 12px', border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 14, outline: 'none', color: S.textPrimary, boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 13, fontWeight: 500, color: S.textSecondary, display: 'block', marginBottom: 6 }}>{t('screens.apiKeys.environment')}</label>
                    <select value={newKeyEnv} onChange={e => setNewKeyEnv(e.target.value)}
                      style={{ width: '100%', padding: '10px 12px', border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 14, outline: 'none', color: S.textPrimary }}>
                      <option value="production">{t('screens.apiKeys.envProduction')}</option>
                      <option value="development">{t('screens.apiKeys.envDevelopment')}</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: 24 }}>
                    <label style={{ fontSize: 13, fontWeight: 500, color: S.textSecondary, display: 'block', marginBottom: 8 }}>{t('screens.apiKeys.permissions')}</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {ALL_PERMISSIONS.map(p => (
                        <button key={p} onClick={() => togglePerm(p)}
                          style={{ padding: '5px 10px', border: `1px solid ${newKeyPerms.includes(p) ? S.burgundy : S.borderLight}`, borderRadius: 6, background: newKeyPerms.includes(p) ? `${S.burgundy}10` : '#fff', fontSize: 12, cursor: 'pointer', color: newKeyPerms.includes(p) ? S.burgundy : S.textSecondary, fontFamily: 'monospace' }}>
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                    <BtnSecondary onClick={() => setShowModal(false)}>{t('screens.apiKeys.cancel')}</BtnSecondary>
                    <BtnPrimary onClick={handleCreate} disabled={!newKeyName.trim() || creating}>{creating ? t('screens.apiKeys.creating') : t('screens.apiKeys.createKey')}</BtnPrimary>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 10, padding: 16, marginBottom: 20 }}>
                    <div style={{ fontSize: 13, color: '#065f46', marginBottom: 8, fontWeight: 500 }}>{t('screens.apiKeys.copyKeyNow')}</div>
                    <code style={{ fontSize: 13, fontFamily: 'monospace', color: S.textPrimary, wordBreak: 'break-all' }}>{createdKey}</code>
                  </div>
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                    <BtnSecondary onClick={() => handleCopy('new', createdKey)}>
                      <Icon name="link" size={14} />{copiedId === 'new' ? t('screens.apiKeys.copied') : t('screens.apiKeys.copyKey')}
                    </BtnSecondary>
                    <BtnPrimary onClick={() => { setShowModal(false); setCreatedKey(null); }}>{t('screens.apiKeys.done')}</BtnPrimary>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default APIKeysScreen;
